import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FcCheckmark, FcCancel } from 'react-icons/fc';
import { FaBoxes, FaDollyFlatbed } from 'react-icons/fa';
import { PropagateLoader } from 'react-spinners';

import BreadCrumb from '~/components/BreadCrumb';
import ConfirmDeleteDialog from '~/components/Dialogs/ConfirmDelete';
import DefaultInput from '~/components/Inputs/Default';
import DeleteButton from '~/components/Buttons/Delete';
import EditButton from '~/components/Buttons/Edit';
import NewButton from '~/components/Buttons/New';
import Pagination from '~/components/Pagination';

import { useToast } from '~/hooks/toast';

import api from '~/services/api';

import {
  Container,
  FilterContent,
  Table,
  Thead,
  Tbody,
  Th,
  TbodyTr,
  Td,
  ButtonsContent,
} from './styles';

interface IMaterial {
  id: string;
  name: string;
  erp_code: string;
  active: boolean;
}

const AMOUNT_PER_PAGE = 5;

const List: React.FC = () => {
  const { addToast } = useToast();
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [filter, setFilter] = useState('');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [materials, setMaterials] = useState<IMaterial[]>();
  const [loading, setLoading] = useState<boolean>(true);
  const [materialIdDelete, setMaterialIdDelete] = useState('');
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [titleDelete, setTitleDelete] = useState('');
  const [subTitleDelete, setSubTitleDelete] = useState('');

  useEffect(() => {
    const loadMaterials = async () => {
      await api
        .get(
          `/items/materials?page=${page}&filter=${filter}&amount=${AMOUNT_PER_PAGE}`,
        )
        .then(response => {
          setMaterials(response.data[0]);
          setCount(response.data[1]);
          setLoading(false);
        });
    };

    loadMaterials();
  }, [page, filter, lastRefresh]);

  const onDelete = useCallback(
    async (id: string) => {
      try {
        await api.delete(`/items/materials/${id}`).then(() => {
          setPage(0);
          setLastRefresh(new Date());

          addToast({
            type: 'success',
            title: 'Material excluído!',
            description: 'Material excluído com sucesso.',
          });
        });
      } catch (error: any) {
        addToast({
          type: 'error',
          title: 'Ocorreu um erro!',
          description: error.response.data.message,
        });
      }
      setMaterialIdDelete('');
      setTitleDelete('');
      setSubTitleDelete('');
    },
    [addToast],
  );

  function handleTable(): React.ReactNode {
    if (materials) {
      if (materials.length > 0) {
        return (
          <Table>
            <Thead>
              <tr>
                <Th width="20%" textAlign="left">
                  Código ERP
                </Th>
                <Th width="50%" textAlign="left">
                  Nome
                </Th>
                <Th width="20%" textAlign="center">
                  Situação
                </Th>
                <Th width="10%" textAlign="center" />
              </tr>
            </Thead>
            <Tbody>
              {loading && (
                <tr>
                  <Td colSpan={4} padding="1rem" textAlign="center">
                    <PropagateLoader color="#276c8d" />
                  </Td>
                </tr>
              )}
              {materials?.map(material => (
                <TbodyTr key={material.id}>
                  <Td width="20%" textAlign="left" heading="CÓDIGO ERP">
                    {material.erp_code}
                  </Td>
                  <Td width="50%" textAlign="left" heading="NOME">
                    {material.name}
                  </Td>
                  <Td width="20%" textAlign="center" heading="SITUAÇÃO">
                    {material.active ? (
                      <FcCheckmark size={22} />
                    ) : (
                      <FcCancel size={22} />
                    )}
                  </Td>
                  <Td
                    width="10%"
                    textAlign="center"
                    display="flex"
                    heading={' '}
                  >
                    <Link
                      to={{
                        pathname: `/items/materials/edit/${material.id}`,
                        state: material,
                      }}
                    >
                      <EditButton />
                    </Link>

                    <DeleteButton
                      marginLeft="0.5rem"
                      onClick={() => {
                        setMaterialIdDelete(material.id);
                        setIsOpenDelete(true);
                        setTitleDelete(
                          `Deseja realmente excluir o item ${material.name}?`,
                        );
                        setSubTitleDelete(
                          'Esta operação não poderá ser desfeita!',
                        );
                      }}
                    />
                  </Td>
                </TbodyTr>
              ))}
            </Tbody>
          </Table>
        );
      }
    }

    return (
      <Table>
        <Thead>
          <tr>
            <Th width="100%" textAlign="center" />
          </tr>
        </Thead>
        <Tbody>
          <TbodyTr>
            <Td width="100%" textAlign="center">
              Nenhuma informação disponível
            </Td>
          </TbodyTr>
        </Tbody>
      </Table>
    );
  }

  return (
    <Container style={{ minHeight: 600 }}>
      <BreadCrumb
        main="Itens"
        path="/items/main"
        firstChild="Materiais"
        mainIcon={FaBoxes}
        firstChildIcon={FaDollyFlatbed}
        mainColored
      />

      <FilterContent>
        <DefaultInput
          name="search"
          labelText="Pesquisar"
          type="text"
          onChange={event => {
            setFilter(event.target.value);
            setPage(0);
          }}
        />
      </FilterContent>

      {handleTable()}

      <ButtonsContent>
        <Pagination
          page={page}
          count={count}
          amountPerPage={AMOUNT_PER_PAGE}
          setPage={setPage}
        />

        <Link to="/items/materials/new">
          <NewButton />
        </Link>
      </ButtonsContent>

      <ConfirmDeleteDialog
        isOpen={isOpenDelete}
        title={titleDelete}
        subTitle={subTitleDelete}
        setIsOpen={setIsOpenDelete}
        onClose={() => {
          onDelete(materialIdDelete);
        }}
      />
    </Container>
  );
};

export default List;
